import { useEffect, useRef, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '~/lib/utils';
import { useBackdropDismissGuard } from '~/hooks/useBackdropDismissGuard';
import { useLockBodyScroll } from '~/hooks/useLockBodyScroll';

type ModalVariant = 'default' | 'large-padding' | 'glass';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  maxWidth?: string;
  children?: ReactNode;
  contentClassName?: string;
  variant?: ModalVariant;
}

export default function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  maxWidth = 'max-w-[680px]',
  children,
  contentClassName = '',
  variant = 'default',
}: ModalProps) {
  const baseHeaderClassName = 'flex items-center relative shrink-0';
  const defaultHeaderClassName = cn(baseHeaderClassName, 'px-3.5 py-5');
  const largePaddingHeaderClassName = cn(baseHeaderClassName, 'px-6 py-6');

  const baseCloseButtonClassName =
    'absolute inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:brightness-90 cursor-pointer transition-all';
  const defaultCloseButtonClassName = cn(baseCloseButtonClassName, 'right-3.5 top-3.5');
  const largePaddingCloseButtonClassName = cn(baseCloseButtonClassName, 'right-6 top-6');

  const baseContentClassName = 'pb-6';
  const defaultContentClassName = cn(baseContentClassName, 'px-6');
  const largePaddingContentClassName = cn(baseContentClassName, 'px-6');

  const isGlass = variant === 'glass';
  const headerClassName =
    variant === 'large-padding' ? largePaddingHeaderClassName : defaultHeaderClassName;
  const closeButtonClassName =
    variant === 'large-padding' ? largePaddingCloseButtonClassName : defaultCloseButtonClassName;
  const glassContentClassName = cn(baseContentClassName, 'px-3.5');
  const contentPaddingByVariant: Record<ModalVariant, string> = {
    default: defaultContentClassName,
    'large-padding': largePaddingContentClassName,
    glass: glassContentClassName,
  };
  const contentPaddingClassName = contentPaddingByVariant[variant];

  const panelClassName = cn(
    'w-full max-h-[90vh] flex flex-col rounded-4xl relative mx-4 min-w-0',
    isGlass
      ? 'border border-white/70 bg-white/92 shadow-[0_12px_40px_rgba(16,24,40,0.12),0_2px_10px_rgba(16,24,40,0.06)] backdrop-blur-xl'
      : 'bg-white shadow-xl',
  );

  useLockBodyScroll(isOpen);

  const panelRef = useRef<HTMLDivElement>(null);
  const shouldDismissOnBackdropClick = useBackdropDismissGuard(isOpen, panelRef);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          // overlay that animates backdrop blur + opacity
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
          animate={{ opacity: 1, backdropFilter: 'blur(6px)' }}
          exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          onClick={() => {
            if (!shouldDismissOnBackdropClick()) return;
            onClose();
          }}
        >
          {/* semi-transparent color layer behind the modal content */}
          <div className="absolute inset-0 bg-black/40" aria-hidden />

          <motion.div
            ref={panelRef}
            data-testid="modal-panel"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className={cn(maxWidth, panelClassName)}
            onClick={(e) => e.stopPropagation()}
          >
            {isGlass && (
              <>
                <div
                  aria-hidden
                  className="pointer-events-none absolute right-[-72px] top-[-56px] size-[220px] rounded-full bg-bg-core/10 blur-3xl"
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute bottom-[-72px] left-[-48px] size-[160px] rounded-full bg-bg-core/6 blur-3xl"
                />
              </>
            )}
            <div className={cn(headerClassName, isGlass && 'relative z-10')}>
              {title}

              <button onClick={onClose} className={closeButtonClassName} aria-label="Fechar">
                <X size={18} />
              </button>
            </div>

            {subtitle && (
              <div className={cn(contentPaddingClassName, 'pb-2')}>
                <p className="text-sm text-text-secondary">{subtitle}</p>
              </div>
            )}

            <div
              className={cn(
                contentPaddingClassName,
                contentClassName,
                isGlass && 'relative z-10 flex-1 min-h-0 overflow-y-auto',
              )}
            >
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
